import React, { useState } from "react";
import { Modal, Input, Button, message, Checkbox } from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import logo from "../Assests/images/footlogo.png";
import authService from "../Redux/Services/authService";
import { setUserSession } from "../utils/auth";

const LoginModal = ({ isOpen, visible, onClose, onLoginSuccess }) => {
  const isModalVisible = isOpen !== undefined ? isOpen : visible;

  // Views: 'login' | 'signup' | 'forgot'
  const [viewMode, setViewMode] = useState("login");
  const [loading, setLoading] = useState(false);

  // Login form state
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // Signup form state
  const [signupName, setSignupName] = useState("");
  const [signupMobile, setSignupMobile] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  // Forgot password form state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const resetForms = () => {
    setLoginIdentifier("");
    setLoginPassword("");
    setSignupName("");
    setSignupMobile("");
    setSignupEmail("");
    setSignupPassword("");
    setSignupConfirmPassword("");
    setForgotEmail("");
    setForgotOtp(["", "", "", "", "", ""]);
    setOtpSent(false);
    setNewPassword("");
    setViewMode("login");
  };

  const handleModalClose = () => {
    resetForms();
    if (onClose) onClose();
  };

  const handleLoginSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!loginIdentifier.trim()) {
      message.error("Please enter your Email or Mobile Number");
      return;
    }
    if (!loginPassword) {
      message.error("Please enter your Password");
      return;
    }

    setLoading(true);
    try {
      // Base64 encode password as required by API
      const encodedPassword = btoa(unescape(encodeURIComponent(loginPassword)));
      const payload = {
        passWord: encodedPassword,
        userCode: loginIdentifier.trim(),
        roleCode: "CUSTOMER"
      };

      let userData = null;
      let token = null;

      const response = await authService.LoginWithPassword(payload);
      if (response && response.data) {
        userData = response.data.user;
        token = response.data.token || response.data.access_token || response.data.sessionId;
      }

      if (!userData) {
        throw new Error("Invalid credentials");
      }

      // Save user session to localStorage & trigger auth events
      setUserSession(userData, token);

      message.success("Logged in successfully!");
      handleModalClose();

      // Trigger success callback (e.g. redirect to checkout)
      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
    } catch (err) {
      console.error("Login error:", err);
      message.error(err?.response?.data?.message || err?.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!signupName.trim()) {
      message.error("Please enter your full name");
      return;
    }
    if (!signupMobile.trim() || !/^\d{10}$/.test(signupMobile.trim())) {
      message.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!signupEmail.trim() || !signupEmail.includes("@")) {
      message.error("Please enter a valid email address");
      return;
    }
    
    // Password validation regex validation
    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$]).{6,}$/;
    if (!signupPassword) {
      message.error("Please enter a password");
      return;
    }
    if (!passwordRegex.test(signupPassword)) {
      message.error("Password must be at least 6 characters and contain an uppercase, lowercase, number, and a special character (!@#$)");
      return;
    }
    if (signupPassword !== signupConfirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const getFormattedDate = () => {
        const date = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      };

      const formattedNow = getFormattedDate();
      const payload = {
        createdBy: "admin",
        email: signupEmail.trim(),
        firstName: signupName.trim(),
        mobile: signupMobile.trim(),
        modifiedBy: "admin",
        pwdValue: signupPassword,
        rolecode: "CUSTOMER",
        status: 1,
        createdDate: formattedNow,
        modifiedDate: formattedNow
      };

      const response = await authService.registration(payload);
      if (response && response.data) {
        message.success("Account created successfully! Please log in to continue.");
        // Prefill the email in the login form for seamless user experience
        setLoginIdentifier(signupEmail.trim());
        setLoginPassword("");
        // Redirect modal to login view mode
        setViewMode("login");
      } else {
        throw new Error("No data returned from registration server.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      message.error(err?.response?.data?.message || err?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!forgotEmail.trim()) {
      message.error("Please enter your Email or Mobile Number");
      return;
    }
    setLoading(true);
    try {
      setOtpSent(true);
      message.success("Verification OTP sent successfully!");
    } catch (err) {
      message.error("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async () => {
    if (!otpSent) {
      handleSendOtp();
      return;
    }
    if (forgotOtp.join("").length < 6) {
      message.error("Please enter the complete 6-digit OTP");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      message.error("New password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      message.success("Password reset successfully! Please log in.");
      setViewMode("login");
      setLoginIdentifier(forgotEmail);
    } catch (err) {
      message.error("Failed to reset password. Please verify your OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    const newOtp = [...forgotOtp];
    newOtp[index] = value;
    setForgotOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  return (
    <Modal
      open={isModalVisible}
      footer={null}
      onCancel={handleModalClose}
      centered
      width={460}
      className="auth-login-modal"
      destroyOnClose
    >
      <div className="p-2 sm:p-4">
        {/* Brand Header */}
        <div className="flex flex-col items-center justify-center pb-4 border-b border-amber-100">
          <div className="bg-[#4C3000] w-full p-1 rounded-2xl shadow-inner mb-2 flex items-center justify-center">
            <img className="h-24 w-auto object-contain" src={logo} alt="Logo" />
          </div>
          <h2 className="text-2xl font-bold text-[#4C3000]">
            {viewMode === "login" && "Welcome Back"}
            {viewMode === "signup" && "Create an Account"}
            {viewMode === "forgot" && "Reset Password"}
          </h2>
          <p className="text-sm text-gray-500 text-center mt-1">
            {viewMode === "login" && "Log in to continue with your purchase"}
            {viewMode === "signup" && "Register to manage orders & express checkout"}
            {viewMode === "forgot" && "Enter your details to receive an OTP"}
          </p>
        </div>

        {/* LOGIN VIEW */}
        {viewMode === "login" && (
          <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email or Mobile Number <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                prefix={<UserOutlined className="text-gray-400 mr-1" />}
                placeholder="Enter email or 10-digit mobile"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                className="rounded-lg hover:border-[#A17A17] focus:border-[#A17A17]"
                autoFocus
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Password <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setViewMode("forgot")}
                  className="text-xs text-[#A17A17] hover:underline font-medium bg-transparent border-0 p-0 cursor-pointer"
                >
                  Forgot Password?
                </button>
              </div>
              <Input
                size="large"
                type={showLoginPassword ? "text" : "password"}
                prefix={<LockOutlined className="text-gray-400 mr-1" />}
                placeholder="Enter your password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="rounded-lg hover:border-[#A17A17] focus:border-[#A17A17]"
                suffix={
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    icon={showLoginPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  />
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="text-sm text-gray-600"
              >
                Remember me
              </Checkbox>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg text-white font-medium text-base transition-all duration-200 shadow-md ${
                loading
                  ? "bg-amber-700 opacity-70 cursor-not-allowed"
                  : "bg-[#A17A17] hover:bg-[#85630e] active:scale-[0.99] cursor-pointer"
              }`}
            >
              {loading ? "Logging in..." : "Login & Proceed"}
            </button>

            <div className="text-center mt-2 pt-3 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setViewMode("signup")}
                  className="text-[#A17A17] font-semibold hover:underline bg-transparent border-0 cursor-pointer"
                >
                  Sign Up
                </button>
              </p>
            </div>
          </form>
        )}

        {/* SIGNUP VIEW */}
        {viewMode === "signup" && (
          <form onSubmit={handleSignupSubmit} className="flex flex-col gap-3 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                prefix={<UserOutlined className="text-gray-400 mr-1" />}
                placeholder="Your full name"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                className="rounded-lg"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                prefix={<PhoneOutlined className="text-gray-400 mr-1" />}
                placeholder="10-digit mobile number"
                maxLength={10}
                value={signupMobile}
                onChange={(e) => setSignupMobile(e.target.value)}
                className="rounded-lg"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                type="email"
                prefix={<MailOutlined className="text-gray-400 mr-1" />}
                placeholder="name@example.com"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className="rounded-lg"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                type={showSignupPassword ? "text" : "password"}
                prefix={<LockOutlined className="text-gray-400 mr-1" />}
                placeholder="Min 6 characters"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                className="rounded-lg"
                suffix={
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    icon={showSignupPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  />
                }
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                type={showSignupPassword ? "text" : "password"}
                prefix={<LockOutlined className="text-gray-400 mr-1" />}
                placeholder="Confirm your password"
                value={signupConfirmPassword}
                onChange={(e) => setSignupConfirmPassword(e.target.value)}
                className="rounded-lg"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 mt-2 rounded-lg text-white font-medium text-base transition-all duration-200 shadow-md ${
                loading
                  ? "bg-amber-700 opacity-70 cursor-not-allowed"
                  : "bg-[#A17A17] hover:bg-[#85630e] cursor-pointer"
              }`}
            >
              {loading ? "Creating Account..." : "Create Account & Proceed"}
            </button>

            <div className="text-center mt-1 pt-2 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setViewMode("login")}
                  className="text-[#A17A17] font-semibold hover:underline bg-transparent border-0 cursor-pointer"
                >
                  Login
                </button>
              </p>
            </div>
          </form>
        )}

        {/* FORGOT PASSWORD VIEW */}
        {viewMode === "forgot" && (
          <div className="flex flex-col gap-4 mt-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email / Registered Mobile
              </label>
              <Input
                size="large"
                prefix={<MailOutlined className="text-gray-400 mr-1" />}
                placeholder="Enter your email or mobile"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="rounded-lg"
                disabled={otpSent}
              />
            </div>

            {otpSent && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter 6-Digit OTP
                  </label>
                  <div className="flex gap-2 justify-between">
                    {forgotOtp.map((digit, idx) => (
                      <input
                        key={idx}
                        id={`otp-input-${idx}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        className="w-12 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg focus:border-[#A17A17] focus:outline-none bg-amber-50/40"
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <Input.Password
                    size="large"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="rounded-lg"
                  />
                </div>
              </>
            )}

            <button
              type="button"
              onClick={handleResetPasswordSubmit}
              disabled={loading}
              className={`w-full py-3 rounded-lg text-white font-medium text-base transition-all duration-200 shadow-md ${
                loading
                  ? "bg-amber-700 opacity-70 cursor-not-allowed"
                  : "bg-[#A17A17] hover:bg-[#85630e] cursor-pointer"
              }`}
            >
              {loading ? "Processing..." : otpSent ? "Reset Password" : "Send OTP"}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setViewMode("login")}
                className="text-sm text-gray-600 hover:text-[#A17A17] flex items-center justify-center gap-1 mx-auto bg-transparent border-0 cursor-pointer"
              >
                <ArrowLeftOutlined /> Back to Login
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LoginModal;
